import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GoogleLogin } from '@react-oauth/google';
import api from '../api/api.js';
import { Eye, EyeOff } from 'lucide-react';
import styles from './Login.module.css';
import { loginSchema } from '../lib/validations';

const Login = () => {
  const [showPassword, setShowPassword]   = useState(false);
  const [isLoading, setIsLoading]         = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [serverError, setServerError]     = useState('');
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const handleLogin = async (data) => {
    setServerError('');
    setIsLoading(true);
    try {
      const { data: resp } = await api.post('/logeo', {
        mail:     data.email,
        password: data.password,
      });

      // Normalizar el rol (puede venir en distintos campos según la respuesta)
      const userRole = (
        resp.role      ||
        resp.user?.role ||
        resp.user?.Rol  ||
        'client'
      ).toLowerCase();

      localStorage.setItem(
        'currentUser',
        JSON.stringify({
          role:         userRole,
          user:         resp.user,
        })
      );
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new Event('sessionChanged'));

      if (userRole === 'admin') {
        navigate('/admin');
      } else if (userRole === 'empleado' || userRole === 'employee') {
        navigate('/employee');
      } else {
        navigate('/PerfilUsuario');
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.error === 'email_not_verified') {
        setServerError('Debes confirmar tu correo electronico antes de iniciar sesion. Revisa tu bandeja de entrada.');
      } else if (err.response?.status === 401 || err.response?.status === 404) {
        setServerError('Credenciales invalidas. Verifica tu correo y contrasena.');
      } else if (err.response?.status === 429) {
        setServerError('Demasiados intentos fallidos. Espera 15 minutos e intenta de nuevo.');
      } else {
        setServerError('Error de servidor. Intentalo mas tarde.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Login con Google (flujo separado, no usa RHF)
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    setIsGoogleLoading(true);
    setServerError('');
    try {
      const { data } = await api.post('/google_login', { credential: credentialResponse.credential });

      localStorage.setItem(
        'currentUser',
        JSON.stringify({
          role:         data.role,
          user:         data.user,
        })
      );
      window.dispatchEvent(new Event('storage'));

      if (data.role === 'admin') {
        navigate('/admin');
      } else if (data.role === 'empleado') {
        navigate('/employee');
      } else {
        const userIncompleto = !data.user?.completo;
        navigate(userIncompleto ? '/completar-registro' : '/PerfilUsuario');
      }
    } catch (err) {
      setServerError('Falló el inicio de sesión con Google. Intenta con tu correo.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setServerError('El inicio de sesión con Google fue cancelado o falló.');
  };

  return (
    <div className={styles.background}>
      <div className={styles.container}>
        <h2 className={styles.text}>Iniciar Sesión</h2>

        <form onSubmit={handleSubmit(handleLogin)} noValidate className={styles.form}>
          <div className={styles.inputs}>

            {/* EMAIL */}
            <div className={styles.inputGroup}>
              <label htmlFor="email" className={styles.label}>Correo Electrónico</label>
              <input
                id="email"
                type="email"
                placeholder="correo@ejemplo.com"
                className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                aria-describedby={errors.email ? 'email-error' : undefined}
                aria-invalid={errors.email ? 'true' : undefined}
                {...register('email')}
              />
              {errors.email && (
                <span id="email-error" className={styles.fieldError} role="alert">
                  {errors.email.message}
                </span>
              )}
            </div>

            {/* CONTRASEÑA */}
            <div className={styles.inputGroup}>
              <label htmlFor="password" className={styles.label}>Contraseña</label>
              <div className={styles.inputWrapper}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Tu contraseña"
                  className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  aria-invalid={errors.password ? 'true' : undefined}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className={styles.iconButton}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <span id="password-error" className={styles.fieldError} role="alert">
                  {errors.password.message}
                </span>
              )}
            </div>
          </div>

          {/* Error de servidor */}
          {serverError && <p className={styles.errorText}>{serverError}</p>}

          <div className={styles.forgotPassword}>
            ¿Olvidó su contraseña?{' '}
            <span
              className={styles.resetLink}
              onClick={() => navigate('/restablecer-contrasena')}
            >
              Haga click aquí para restablecerla
            </span>
          </div>

          <div className={styles.submitContainer}>
            <button
              type="submit"
              className={styles.submit}
              disabled={isLoading || isGoogleLoading}
            >
              {isLoading ? 'Iniciando Sesión...' : 'Iniciar Sesión'}
            </button>
          </div>

          <p className={styles.infoText}>
            Tu sesión se almacena de forma segura en una cookie HttpOnly, inaccesible para JavaScript.
          </p>
        </form>

        {/* Google Login — flujo independiente fuera del form */}
        <div className={styles.orSeparator}>o</div>
        <div className={styles.googleLogin}>
          <GoogleLogin
            onSuccess={handleGoogleLoginSuccess}
            onError={handleGoogleLoginError}
            useOneTap={false}
            text="signin_with"
            shape="rectangular"
            width="100%"
          />
        </div>

        <p className={styles.noAccount}>
          ¿No tienes cuenta?{' '}
          <span className={styles.resetLink} onClick={() => navigate('/Register')}>
            Regístrate aquí.
          </span>
        </p>
      </div>
    </div>
  );
};

export default Login;
